import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../../firebase";
import AnimatedText from "../../user/file_management/component/AnimatedText";

const COLORS = [
  "#2389ff",
  "#25c2a0",
  "#ffc858",
  "#ef5350",
  "#4f83ff",
  "#f7c77e",
  "#9f92e4",
  "#fa6e7b",
  "#6ec6ff",
];

export default function UserAnalyticsDashboard() {
  const theme = useTheme();
  const [totalUsers, setTotalUsers] = useState(0);
  const [usersByDepartment, setUsersByDepartment] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchUserData() {
      setLoading(true);
      try {
        // Query users where role is "user"
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("role", "==", "user"));
        const querySnapshot = await getDocs(q);

        const users = [];
        querySnapshot.forEach((doc) => {
          users.push(doc.data());
        });

        setTotalUsers(users.length);

        // Aggregate users by department
        const departmentCounts = {};
        users.forEach((user) => {
          const dept = user.department || "Unknown";
          if (departmentCounts[dept]) {
            departmentCounts[dept]++;
          } else {
            departmentCounts[dept] = 1;
          }
        });

        // Define all categories to always show
        const allCategories = [
          "Engineering",
          "HSC",
          "MPS",
          "Computer Studies and Multimedia Arts",
        ];

        // Prepare data for bar chart, ensuring all categories are included
        const departmentData = allCategories.map((category) => ({
          name: category,
          value: departmentCounts[category] || 0,
        }));

        setUsersByDepartment(departmentData);
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    }
    console.log("[DEBUG] usersByDepartment:", usersByDepartment);
    fetchUserData();
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: theme.palette.background.default,
        px: { xs: 1, md: 4 },
        py: { xs: 2, md: 6 },
      }}
    >
      <AnimatedText text="User Analytics Dashboard" />
      {loading ? (
        <Typography variant="h6" align="center" sx={{ mt: 4 }}>
          Loading...
        </Typography>
      ) : (
        <Card
          elevation={0}
          sx={{
            background: theme.palette.background.paper,
            borderRadius: 3,
            minHeight: 400,
            padding: 3,
            maxWidth: 700,
            margin: "0 auto",
          }}
        >
          <CardContent>
            <Typography
              variant="subtitle1"
              sx={{
                color: theme.palette.text.primary,
                mb: 2,
                fontWeight: 600,
                letterSpacing: 0.1,
                background: "#171A1C",
                textAlign: "center",
                borderRadius: 2,
                padding: 1,
              }}
            >
              No. of Users Per Department
            </Typography>

            <Box sx={{ width: "100%", height: 450 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={usersByDepartment}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  style={{ backgroundColor: "#ffffff" }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    label={{
                      value: "No. of Users",
                      position: "insideMiddle",

                      dy: 20,
                    }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={200}
                    interval={0}
                    tick={{ fill: "#000" }}
                    label={{
                      value: "Departments",
                      angle: -90,
                      position: "insideLeft",
                      offset: 10,
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#23263b",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  />
                  <Bar dataKey="value" fill="#1e90ff">
                    <LabelList dataKey="value" position="insideMiddle" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Typography
              variant="subtitle2"
              sx={{
                mt: 2,
                fontWeight: 600,
                textAlign: "center",
                fontSize: 20,
                color: theme.palette.text.primary,
              }}
            >
              Total Number Of Users: {totalUsers}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
